import { SelectionState } from "@monolith-forensics/monolith-ui";
import { GetEvidenceQuery } from "../evidence.api.types";
import { usePermissions } from "@/hooks";
import { useSnackbar } from "notistack";
import { useCaseContext } from "@/Features/Cases";
import { useState } from "react";
import { ChainOfCustodyFormValues } from "@/Features/ChainOfCustody";
import { DefaultBulkCocFormValues } from "../Modals";
import { LocationsAPI } from "@/Features/Locations";
import EvidenceCocAPI from "@/api/evidence/coc";
import { useFeatures } from "@/contexts/FeaturesContext";

export enum BulkCocAction {
  Intake = "bulk-coc-intake",
  Move = "bulk-coc-move",
  Checkout = "bulk-coc-checkout",
  Release = "bulk-coc-release",
  Destroy = "bulk-coc-destroy",
  CreateIntakeForm = "create-intake-form",
  CreateReleaseForm = "create-release-form",
  ExportCocReports = "export-coc-reports",
}

export const useBulkCocActions = (
  selectionState: SelectionState,
  resolvedQuery: GetEvidenceQuery
) => {
  const { currentUser, hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { hasFeatures, FEATURES } = useFeatures();
  const { data: caseData } = useCaseContext() || {};
  const inCaseContext = !!caseData; // Whether the user is in a case context

  const bulkCocEnabled =
    hasFeatures([FEATURES.EVIDENCE_ALLOW_ALL_BULK_COC]) || !!inCaseContext;

  const canBulkIntake = hasPermission(
    MONOLITH_PERMISSIONS.EVIDENCE_BULK_INTAKE
  );
  const canBulkMove = hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_BULK_MOVE);
  const canBulkRelease = hasPermission(
    MONOLITH_PERMISSIONS.EVIDENCE_BULK_RELEASE
  );
  const canBulkDestroy = hasPermission(
    MONOLITH_PERMISSIONS.EVIDENCE_BULK_RELEASE
  );

  const [showBulkCocModal, setShowBulkCocModal] = useState(false);
  const [bulkCocType, setBulkCocType] =
    useState<ChainOfCustodyFormValues["type"]>();
  const [bulkCocFormData, setBulkCocFormData] =
    useState<DefaultBulkCocFormValues>();

  const [showCocReportsModal, setShowCocReportsModal] = useState(false);
  const [cocReportsData, setCocReportsData] = useState<{
    filename: string;
    signedUrl: string;
  }>();

  const handleBulkCocAction = (action: BulkCocAction) => {
    const selectionTotal = selectionState?.totalSelected || 0;
    if (selectionTotal === 0) {
      enqueueSnackbar("Please select at least one evidence item.", {
        variant: "warning",
      });
      return;
    }
    switch (action) {
      case BulkCocAction.Intake:
        setBulkCocType("Intake");
        setBulkCocFormData({
          coc: {
            type: "Intake",
            timestamp: new Date().toISOString(),
            custody_to: {
              label: currentUser.full_name,
              value: currentUser.location_id,
              data: {
                location_id: currentUser.location_id,
                location_name: currentUser.full_name,
              },
            },
          },
        });
        setShowBulkCocModal(true);
        break;
      case BulkCocAction.Move:
        setBulkCocType("Move");
        setBulkCocFormData({
          coc: {
            type: "Move",
            timestamp: new Date().toISOString(),
            custody_from: {
              label: "Previous Location",
              value: "Previous Location",
              data: {
                location_id: 0,
                location_name: "Previous Location",
              },
            },
          },
        });
        setShowBulkCocModal(true);
        break;
      case BulkCocAction.Checkout:
        setBulkCocType("Checkout");
        setBulkCocFormData({
          coc: {
            type: "Checkout",
            timestamp: new Date().toISOString(),
            custody_to: {
              label: currentUser.full_name,
              value: currentUser.location_id,
              data: {
                location_id: currentUser.location_id,
                location_name: currentUser.full_name,
              },
            },
            custody_from: {
              label: "Previous Location",
              value: "Previous Location",
              data: {
                location_id: 0,
                location_name: "Previous Location",
              },
            },
          },
        });
        setShowBulkCocModal(true);
        break;
      case BulkCocAction.Release:
        setBulkCocType("Release");
        setBulkCocFormData({
          coc: {
            type: "Release",
            timestamp: new Date().toISOString(),
            custody_from: {
              label: currentUser.full_name,
              value: currentUser.location_id,
              data: {
                location_id: currentUser.location_id,
                location_name: currentUser.full_name,
              },
            },
          },
        });
        setShowBulkCocModal(true);
        break;
      case BulkCocAction.Destroy:
        setBulkCocType("Destroy");
        setBulkCocFormData({
          coc: {
            type: "Destroy",
            timestamp: new Date().toISOString(),
            custody_from: {
              label: currentUser.full_name,
              value: currentUser.location_id,
              data: {
                location_id: currentUser.location_id,
                location_name: currentUser.full_name,
              },
            },
            custody_to: {
              label: LocationsAPI.metaLocations.destroyed.location_name,
              value: LocationsAPI.metaLocations.destroyed.location_id,
              data: LocationsAPI.metaLocations.destroyed,
            },
          },
        });
        setShowBulkCocModal(true);
        break;
      case BulkCocAction.CreateIntakeForm:
        if (!inCaseContext) {
          break;
        }
        EvidenceCocAPI.createEvidenceMultiIntakePDF({
          selectionState: {
            selectionStatus: selectionState?.selectionStatus,
            selectedItems: selectionState?.selectedRowKeys || [],
            excludedItems: selectionState?.excludedRowKeys || [],
            query: resolvedQuery,
          },
        });
        break;
      case BulkCocAction.CreateReleaseForm:
        if (!inCaseContext) {
          break;
        }
        EvidenceCocAPI.createEvidenceMultiReleasePDF({
          selectionState: {
            selectionStatus: selectionState?.selectionStatus,
            selectedItems: selectionState?.selectedRowKeys || [],
            excludedItems: selectionState?.excludedRowKeys || [],
            query: resolvedQuery,
          },
        });
        break;
      case BulkCocAction.ExportCocReports:
        if (!selectionState) break;
        setShowCocReportsModal(true);
        EvidenceCocAPI.exportMultipleEvidenceCocPDF({
          selectionState: {
            selectionStatus: selectionState.selectionStatus,
            selectedItems: selectionState?.selectedRowKeys || [],
            excludedItems: selectionState?.excludedRowKeys || [],
            totalSelected: selectionState?.totalSelected || 0,
            query: resolvedQuery,
          },
        }).then((res) => {
          setCocReportsData(res);
        });
        break;
      default:
        break;
    }
  };

  const cocMenuItems = {
    label: "Chain of Custody",
    value: "chain-of-custody",
    visible: bulkCocEnabled,
    items: [
      {
        label: "Bulk Intake Evidence",
        value: "bulk-coc-intake",
        visible: canBulkIntake,
        onClick: () => {
          handleBulkCocAction(BulkCocAction.Intake);
        },
      },
      {
        label: "Bulk Move Evidence",
        value: "bulk-coc-move",
        visible: canBulkMove,
        onClick: () => {
          handleBulkCocAction(BulkCocAction.Move);
        },
      },
      {
        label: "Bulk Checkout Evidence",
        value: "bulk-coc-checkout",
        visible: canBulkMove,
        onClick: () => {
          handleBulkCocAction(BulkCocAction.Checkout);
        },
      },
      {
        label: "Bulk Release Evidence",
        value: "bulk-coc-release",
        visible: canBulkRelease,
        onClick: () => {
          handleBulkCocAction(BulkCocAction.Release);
        },
      },
      {
        label: "Bulk Destroy Evidence",
        value: "bulk-coc-destroy",
        visible: canBulkDestroy,
        onClick: () => {
          handleBulkCocAction(BulkCocAction.Destroy);
        },
      },
      {
        label: "Export COC Reports",
        value: "export-coc-reports",
        onClick: () => {
          handleBulkCocAction(BulkCocAction.ExportCocReports);
        },
      },
      {
        label: "Create Intake Form",
        value: "create-intake-form",
        visible: inCaseContext,
        onClick: () => {
          handleBulkCocAction(BulkCocAction.CreateIntakeForm);
        },
      },
      {
        label: "Create Release Form",
        value: "create-release-form",
        visible: inCaseContext,
        onClick: () => {
          handleBulkCocAction(BulkCocAction.CreateReleaseForm);
        },
      },
    ],
  };

  return {
    showBulkCocModal,
    setShowBulkCocModal,
    bulkCocType,
    setBulkCocType,
    bulkCocFormData,
    setBulkCocFormData,
    showCocReportsModal,
    setShowCocReportsModal,
    cocReportsData,
    setCocReportsData,
    handleBulkCocAction,
    cocMenuItems,
    enabled: bulkCocEnabled,
  };
};
